import { useState } from "react";
import { Form, Select, Button, Space, Card, message, DatePicker } from "antd";
import { FileAddOutlined } from "@ant-design/icons";
import lang from "src/lang";
import coreFunctions from "src/common/utils/coreFunctions";
import apiRequest from "src/common/utils/apiRequest";
import { SelectType } from "@modules/appointment/types";
import DataFormatHelper from "src/common/utils/DataFormatHelper";

interface AddAppointmentProps {
  onCancel: () => void;
  form: any;
  actionRef: any;
}

const AddAppointment: React.FC<AddAppointmentProps> = ({
  form,
  onCancel,
  actionRef,
}) => {
  const [customerList, setCustomerList] = useState<SelectType[]>([]);
  const [statusList, setStatusList] = useState<SelectType[]>([]);
  const [jobCategoryList, setJobCategoryList] = useState<SelectType[]>([]);
  const [vehicleList, setVehicleList] = useState<SelectType[]>([]);
  const [customer, setCustomer] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const CORE_FUNCTIONS = new coreFunctions();

  const saveAppointmentHandler = async () => {
    setSaving(true);
    try {
      await form.validateFields();
      const values = DataFormatHelper.payloadData(form.getFieldsValue(), [
        "customer",
        "status",
        "vehicle",
        "job_category",
      ]);

      const API_REQUEST = new apiRequest("appointment/add", true);

      API_REQUEST.send(values, (data: { status: string; message: string }) => {
        if (data.status === "success") {
          actionRef.current?.reset?.();
          actionRef.current?.reload();
          message.success(lang.get("appointments", [data.message]), 2);
          onCancel();
        } else {
          message.error(lang.get("appointments", [data.message]), 2);
        }
      });
    } catch (error) {
      message.error("Please check in all required fields.");
    }
    setSaving(false);
  };

  const cancelHandler = () => {
    onCancel();
  };

  return (
    <div>
      <Card
        title={lang.get("appointments", ["Add Appointment"])}
        extra={<FileAddOutlined />}
        style={{ marginBottom: 16 }}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name={"customer"}
            label={
              <span style={{ fontSize: "14px" }}>
                {lang.get("appointments", ["Customer Name"])}
              </span>
            }
            rules={[{ required: true, message: "Please select a customer!" }]}
          >
            <Select
              showSearch
              placeholder={lang.get("appointments", ["Customer Name"])}
              filterOption={false}
              onChange={(e) => setCustomer(e)}
              size="large"
              onSearch={(e) => {
                CORE_FUNCTIONS.searchOptions(
                  e,
                  "clients",
                  "clients",
                  setCustomerList
                );
              }}
              onClick={() => {
                CORE_FUNCTIONS.searchOptions(
                  "",
                  "clients",
                  "clients",
                  setCustomerList
                );
              }}
              options={customerList.map((customer) => ({
                label: customer.name,
                value: customer.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name={"vehicle"}
            label={
              <span style={{ fontSize: "14px" }}>
                {lang.get("appointments", ["Vehicle Number"])}
              </span>
            }
            rules={[{ required: true, message: "Please select a vehicle!" }]}
          >
            <Select
              showSearch
              placeholder={lang.get("appointments", ["Vehicle Number"])}
              filterOption={false}
              size="large"
              onSearch={(e) => {
                CORE_FUNCTIONS.searchOptions(
                  e,
                  "vehicles",
                  "vehicles",
                  setVehicleList,
                  { customer: customer }
                );
              }}
              onClick={() => {
                CORE_FUNCTIONS.searchOptions(
                  "",
                  "vehicles",
                  "vehicles",
                  setVehicleList,
                  { customer: customer }
                );
              }}
              options={vehicleList.map((vehicle) => ({
                label: vehicle.name,
                value: vehicle.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name={"appointment_date"}
            label={
              <span style={{ fontSize: "14px" }}>
                {lang.get("appointments", ["Appointment Date"])}
              </span>
            }
            rules={[
              { required: true, message: "Please select an appointment date!" },
            ]}
          >
            <DatePicker size="large" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name={"service_category"}
            label={
              <span style={{ fontSize: "14px" }}>
                {lang.get("appointments", ["Service Category"])}
              </span>
            }
            rules={[
              { required: true, message: "Please select a service category!" },
            ]}
          >
            <Select
              showSearch
              placeholder={lang.get("appointments", ["Vehicle Number"])}
              filterOption={false}
              size="large"
              onSearch={(e) => {
                CORE_FUNCTIONS.searchOptions(
                  e,
                  "job",
                  "categories",
                  setJobCategoryList
                );
              }}
              onClick={() => {
                CORE_FUNCTIONS.searchOptions(
                  "",
                  "job",
                  "categories",
                  setJobCategoryList
                );
              }}
              options={jobCategoryList.map((row) => ({
                label: row.name,
                value: row.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name={"status"}
            label={
              <span style={{ fontSize: "14px" }}>
                {lang.get("appointments", ["Status"])}
              </span>
            }
            rules={[{ required: true, message: "Please select a status!" }]}
          >
            <Select
              showSearch
              placeholder={lang.get("appointments", ["Status"])}
              filterOption={false}
              size="large"
              onSearch={(e) => {
                CORE_FUNCTIONS.searchOptions(
                  e,
                  "appointments",
                  "statuses",
                  setStatusList
                );
              }}
              onClick={() => {
                CORE_FUNCTIONS.searchOptions(
                  "",
                  "appointments",
                  "statuses",
                  setStatusList
                );
              }}
              options={statusList.map((row) => ({
                label: row.name,
                value: row.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Card>

      <Space
        style={{ marginTop: "16px", justifyContent: "flex-end", width: "100%" }}
      >
        <Button type="primary" onClick={saveAppointmentHandler}>
          Save
        </Button>
        <Button type="default" onClick={cancelHandler}>
          Cancel
        </Button>
      </Space>
    </div>
  );
};

export default AddAppointment;
